<template>
    <div>
        <div class = "checkout-summary is-pulled-right is-hidden-mobile">
            <div class="checkout-order-btn" @click="checkValidForm()">
                <BaseButton
                    :disabled="isCheckoutDisabled"
                    :classNames="['button--modifier', 'addToCart']"
                    title="Place Order"
                    @click="placeOrderClick"
                />
            </div>
            <p class="has-text-centered el-spacing">{{ getOrderTime}}</p>
            <div class="price-summary">
                <div class="level">
                    <div class="level-left">
						<span class="level-item"> {{itemCount}} Items </span>
                    </div>
                    <div class="level-right">
                        <span class="level-item"> {{centsToDollars(streamLinedTotals.subTotal)}}</span>
                    </div>
                </div>
				<div class="level" v-if="hasDiscount">
					<div class="level-left">
						<span class="level-item"> Discount </span>
					</div>
					<div class="level-right">
						<span class="discount"> {{ centsToDollars(streamLinedTotals.discount) }}</span>
						<span v-if="discountDisplayType" class="discount">&nbsp;{{ discountDisplayType }}</span>
					</div>
				</div>
                <hr class="dark-hr">
                <div class="is-bold">
                    <div class="level ==" v-if="serviceType === EServiceType.SERVICE_TYPE_DELIVERY">
                        <div class="level-left">
							<span class="level-item"> Delivery Fee </span>
                        </div>
                        <div class="level-right" v-if="deliveryFee !== false">
                            <span class="level-item"> {{centsToDollars(streamLinedTotals.serviceCharge) || 0}}</span>
                        </div>
                    </div>
                    <div class="level">
                        <div class="level-left">
							<span class="level-item"> TAX </span>
                        </div>
                        <div class="level-right">
                            <span class="level-item"> {{centsToDollars(streamLinedTotals.tax) || 0}}</span>
                        </div>
                    </div>
                    <div style="margin-top: 0em;" class="price-summary">
                        <div class="level">
                            <div class="level-left">
                                <span class="level-item">TOTAL</span>
                            </div>
                            <div class="level-right">
                                <span class="level-item">{{ centsToDollars(streamLinedTotals.total) }}</span>
                            </div>
                        </div>
                    </div>
                    <template v-if="paymentMethod && preorder === 0">
                        <hr class="dark-hr">
                        <div class="level">
                            <div class="level-left">
								<span class="level-item"> Payment </span>
                            </div>
                            <div class="level-right">
                                <span class="level-item"> {{paymentMethod.logo}} *{{paymentMethod.accountNumber}}</span>
                            </div>
                        </div>
                    </template>
                </div>
                <hr class="dark-hr">
				<div id="destination-info" class="has-text-centered">
                    <order-destination/>
                </div>
                <hr class="dark-hr">
                <p class="user-instructions has-text-grey has-text-centered" v-if="userInstructions">"{{userInstructions}}"</p>
			</div>
        </div>
        <div v-if="$mq === 'mobile'" class="is-hidden-tablet item-detail-info--bottom">
			<div>
				<p @click="handleMissingInfoClick" class="error" v-if="isCheckoutDisabled">{{displayMissingInfo()}}</p>
				<p v-if="validCheckoutUser">
					{{getServiceTypeName(serviceType)}} for {{getCustomerName()}}
				</p>
				<p class="error" v-if="!validCheckoutUser">
					<a href="/sign-in" @click.prevent="showLogin">Please Sign In</a>
				</p>
            </div>
        </div>
    </div>
</template>

<script>
import * as moment from "moment";
import EventBus from '@EventBus';
import { CART, ACCOUNT_CHECKOUT, LOCATION } from '@/stores/defs';
import { EServiceType } from "@/models/interfaces/Orders/EServiceType";
import { mapGetters, mapActions, mapState } from 'vuex';
import OrderDestination from '@/components/checkout/OrderDestination';
import Login from '@/components/auth/Login.vue';
import checkoutMixin from '@Mixin/checkoutMixin';
import checkoutPricesMixin from '@/components/mixins/checkoutPricesMixin';
import couponMixin from '@Mixin/couponMixin';
import Formatter from "@/utils/Formatter";
import offersMixin from '@Mixin/offersMixin';
import modalMixin from '@/components/mixins/modalMixin';
import timeMixin from '@/components/mixins/timeMixin';
import orderService from "@/services/order.service";
import DateManipulator from '@/utils/DateManipulator';

export default {
	data() {
		return {
			discountDisplayType: null,
		}
	},
	components: {
		'order-destination': OrderDestination,
	},
	mixins: [
		checkoutMixin,
		offersMixin,
		couponMixin,
		checkoutPricesMixin,
		timeMixin,
		modalMixin
	],
    props: {
        address: Object,
        disabled: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapGetters({
            priceTotal: CART.PRICE,
            itemCount: CART.ITEM_COUNT,
            items: CART.ITEMS,
            checkoutTotal: CART.GET_CHECKOUT_TOTAL,
            orderTime: ACCOUNT_CHECKOUT.GET_ORDER_TIME,
            totals: CART.GET_TOTALS,
            location: LOCATION.GET_LOCATION
        }),
        getOrderTime() {
			if (!this.orderTime) {
				return 'N/A';
			}
            let timePrefix =  this.orderTime.format('MM/DD: ');

            return timePrefix + this.displayDateRange(this.orderTime);
		},
		hasDiscount() {
			const discountTotal = this.getDiscountTotal();
			const coupon = this.getCoupon;
			this.resetDisplayType();

			if (coupon && coupon.isForOrder() && coupon.active) {
				this.updateDisplayType(coupon);
				return true;
			}
			if (discountTotal > 0) {
				return true;
			}
			return false;
		},
    },
    data() {
        return {
            EServiceType: EServiceType,
        };
    },
    methods: {
        centsToDollars: Formatter.centsToDollars,
		displayDiscountTotal() {
			const discountTotal = this.getDiscountTotal();
			return Formatter.centsToDollars(discountTotal);
		},
		displayCheckoutTotal() {
			let total = this.checkoutTotal;
			const discount = this.getDiscountTotal();
			total -= discount;
			return Formatter.centsToDollars(total);
		},
		displayMissingInfo() {
			if (this.missingInformation()) {
				return 'Required Contact Details Needed';
			}
			return '';
		},
		updateDisplayType(discount) {
			if (!discount) {
				this.discountDisplayType = null;
				return;
			}
			const percent = discount.getPercentAmount();
			if (percent) {
				this.discountDisplayType = `(${percent}%)`;
			}
		},
		resetDisplayType() {
			this.discountDisplayType = null;
		},
		showLogin() {
			this.triggerModal({
				component: Login,
				title: "Login",
				classes: 'global-modal',
				modalName: "loginModal",
				componentProperties: {
					routeTo: '/checkout',
				},
			});
		},
		handleMissingInfoClick() {
			window.scrollTo(0,0);
		},
		placeOrderClick() {
			// TODO BaseButton disabled should not adapt button:disabled behavior
			this.$emit('placeOrder');
		},
	},
}
</script>

<style lang="scss">
    @import "globalStyles";
    .checkout-summary {
        background-color: $greyBackground;
        min-height: 20em;
        margin-right: 1em;
        padding: 0.5em 1.5em;
		width: 90%;
		
		.discount {
			color: $discountColor;
		}
    }

    .price-summary {
        margin: 0 auto;
        margin-top: 2em;
    }

    .dark-hr { 
        background-color: #cfcfcf;
        margin: 0.7em auto;
        height: 1px;
    }

    .price-summary .level {
        margin-bottom: 0.5em;
    }
    .el-spacing {
        margin: 1em;
    }

    #destination-info .customer-name {
        font-weight: 500;
    }

    .user-instructions {
        font-style: italic;
		padding: 1em 0em;
		word-break: break-word;
    }

    @include desktop {
        .checkout-summary {
            position: sticky;
            top: 0;
        }
        .checkout-order-btn {
            padding-top: 1em;
        }
	}

	@include mobile {
		.checkout-summary {
			display: none;
		}
	}

</style>



// WEBPACK FOOTER //
// src/components/checkout/CheckoutSidebar.vue